import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '~/lib/api-client';
import { budgetKeys, codingKeys } from '~/lib/query-keys';
import { budgetRefused } from '~/features/limits/budget-refusal';
import { useCodingSessionCost } from '~/hooks/queries/use-coding-sessions';
import { useMarkCodingSessionSeen } from '~/hooks/mutations/use-coding-mutations';
import {
  appendEvents,
  buildTranscript,
  computeQueueBusy,
  deriveContextUsage,
  deriveRunActivity,
  deriveRunSummary,
  deriveSessionStats,
  countEndedRuns,
  deriveStatus,
  reconcileWithServerLiveness,
  deriveWaitingReason,
  deriveUploads,
  findBlockingQuestionItem,
  hasUnansweredQuestion,
  maxSeq,
  type CodingEvent,
  type CodingEventName,
  type CodingQuestionAnswer,
  type CodingSessionView,
  type CodingStatus,
  type WaitingReason,
} from './coding-events';
import type { ChatAttachment } from '~/features/chat/chat-input';
import { toLocalActivityAt } from './build-liveness';
import type { WizardEntry } from '../questions/answer-wizard-model';

const NAME_PROJECT_TOOL = 'mcp__ask__name_project';

const TAPE_EVENT_NAMES: CodingEventName[] = [
  'user_message',
  'assistant_text',
  'tool_use',
  'tool_result',
  'run_started',
  // FRONT-987 wrote a transcript marker for this ("Reconnected after a backend
  // restart") and a run_resumed branch exists in deriveStatus/deriveRunActivity,
  // but the name was never registered here — EventSource only delivers events
  // it has a named listener for, so every one has been silently dropped and
  // both were dead in the browser. Found in review of FRONT-1746.
  'run_resumed',
  'run_finished',
  'run_failed',
  'run_interrupted',
  'upload_removed',
  'context_cleared',
  'question_asked',
  'question_answered',
];

interface SummaryResponse {
  summary: string | null;
  refreshed: boolean;
}

/** FRONT-3175: the exec summary under the Build's title. `refreshed` means
 *  EDDY just rewrote it on this open, so the view types it out. */
export interface SessionSummaryState {
  text: string | null;
  refreshed: boolean;
  pending: boolean;
}

interface SessionResponse {
  session: CodingSessionView;
  lastSeq: number;
  serverNow?: string;
}

function onLocalClock({ session, serverNow }: SessionResponse): CodingSessionView {
  return { ...session, lastActivityAt: toLocalActivityAt(session.lastActivityAt, serverNow, Date.now()) };
}

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 10_000;

/**
 * One attached coding session (FRONT-333): fetches the session row, opens
 * the SSE tape stream (full replay from 0, then live tail) and keeps the
 * local tape in sync. The window is a VIEW onto the run — unmounting closes
 * the socket but never the run; re-attaching replays and catches up.
 *
 * Stream lifecycle is managed manually instead of relying on EventSource's
 * built-in retry: the server intentionally closes idle streams (no active
 * run) a few seconds after replay, and the native auto-reconnect would turn
 * that into a tight reconnect loop with a stale `after` cursor. We reconnect
 * with the current cursor, and only while a run is in flight.
 */
/** FRONT-2935: what POST /update answers when it is not a refusal (refusals throw with their sentence). */
export type DraftUpdateResult = { status: 'updated' | 'up-to-date' } | { status: 'reworking'; files: string[]; more: number; lastSeq: number };

/** What the answers route accepts (FRONT-3155: entries may be skipped or null = left open, plus a note). */
export type AnswerRequestBody =
  | { answers: Array<CodingQuestionAnswer | WizardEntry>; note?: string }
  | { skip: true }
  | { stopGuidedBuild: true }
  | { dismiss: true };

export function useCodingSession(sessionId: string | null) {
  const queryClient = useQueryClient();
  // FRONT-2937: after a turn refused for budget, so the budget card and composer lock show at once.
  const refreshBudget = useCallback(() => void queryClient.invalidateQueries({ queryKey: budgetKeys.all }), [queryClient]);
  const markSeen = useMarkCodingSessionSeen();
  const [session, setSession] = useState<CodingSessionView | null>(null);
  const [events, setEvents] = useState<CodingEvent[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  // FRONT-1898: the composer's local "the user already decided this is
  // over" flag, ported from chat's `manuallyStopped` (use-agent-chat.ts).
  // Set the instant `interrupt()` is called, so the busy/Stop-vs-Send state
  // and the queued-draft gate free immediately rather than waiting for the
  // server row to actually leave "running" — which can lag by however long
  // the abandoned run takes to notice the abort (up to the sandbox tools'
  // own timeouts today) or, if the interrupt silently no-ops, forever.
  // Reset at the one choke point a genuinely new turn starts through
  // (`send`) and on session change, mirroring the chat pattern exactly.
  const [manuallyStopped, setManuallyStopped] = useState(false);
  // FRONT-3175: the tape position when this window opened — what came before
  // it is "earlier history" the transcript folds — and the exec summary shown
  // under the title. `summaryPending` is true while EDDY rewrites it.
  const [openedAtSeq, setOpenedAtSeq] = useState<number | null>(null);
  const [summary, setSummary] = useState<SessionSummaryState>({ text: null, refreshed: false, pending: false });
  useEffect(() => {
    setManuallyStopped(false);
  }, [sessionId]);

  const esRef = useRef<EventSource | null>(null);
  const cursorRef = useRef(0);
  const backoffRef = useRef(RECONNECT_BASE_MS);
  const reconnectTimerRef = useRef<number | null>(null);
  const workingRef = useRef(false);
  // The session this hook is currently wired to — guards async callbacks
  // (fetches, reconnect timers) from acting after a session switch.
  const attachedIdRef = useRef<string | null>(null);
  // FRONT-1681 (third review round): the tape's high-water mark AS OF THE
  // ATTACH GET (the same `lastSeq` the attach fetch already receives and
  // used to discard) — the boundary between "existed before this window
  // opened" and "arrived while it was open". `null` while unknown (before
  // the GET resolves, or after a session switch resets it) — see the SSE
  // listener below for how that's handled.
  const attachLastSeqRef = useRef<number | null>(null);
  const namingCallsRef = useRef(new Set<string>());

  const closeStream = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    esRef.current?.close();
    esRef.current = null;
  }, []);

  // The lightweight half of a session refresh — just re-GET the row and
  // update local state. Shared by `refreshSession` (below, which additionally
  // invalidates the sessions list — right for a terminal tape event, wasteful
  // if fired every 2s) and the BUG-037 poll, which only needs `session.status`
  // and has nothing to do with the list.
  const fetchSession = useCallback(async (id: string) => {
    try {
      const data = await api.get<SessionResponse>(`/api/coding/sessions/${id}`);
      if (attachedIdRef.current === id) setSession(onLocalClock(data));
    } catch {
      /* transient — the next poll tick or terminal event retries */
    }
  }, []);

  const refreshSession = useCallback(
    async (id: string) => {
      await fetchSession(id);
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      // FRONT-2935: a finished turn may have brought the app's latest changes in.
      queryClient.invalidateQueries({ queryKey: codingKeys.draftUpdate(id) });
    },
    [fetchSession, queryClient],
  );

  const openStream = useCallback(
    (id: string, after: number) => {
      closeStream();
      const es = new EventSource(`/api/coding/sessions/${id}/stream?after=${after}`, { withCredentials: true });
      esRef.current = es;

      for (const name of TAPE_EVENT_NAMES) {
        es.addEventListener(name, (raw) => {
          const msg = raw as MessageEvent<string>;
          const seq = Number(msg.lastEventId);
          let payload: Record<string, unknown> = {};
          try {
            payload = JSON.parse(msg.data) as Record<string, unknown>;
          } catch {
            /* keep the event with an empty payload rather than dropping the seq */
          }
          if (Number.isFinite(seq) && seq > cursorRef.current) cursorRef.current = seq;
          backoffRef.current = RECONNECT_BASE_MS;
          setEvents((prev) => appendEvents(prev, [{ seq, name, payload }]));
          // FRONT-2721: a live step is activity now; the 2s poll only confirms it.
          if (attachLastSeqRef.current != null && seq > attachLastSeqRef.current) {
            setSession((s) => (s ? { ...s, lastActivityAt: new Date().toISOString() } : s));
          }
          // EDDY named the project (eddy, 2026-10-26): re-read the row so the
          // title and the builds list show the name as soon as it is set.
          if (name === 'tool_use' && payload.tool === NAME_PROJECT_TOOL && typeof payload.toolUseId === 'string') {
            namingCallsRef.current.add(payload.toolUseId);
          } else if (name === 'tool_result' && namingCallsRef.current.has(payload.toolUseId as string)) {
            namingCallsRef.current.delete(payload.toolUseId as string);
            // A replayed one on attach costs one GET of a row already fresh.
            void refreshSession(id);
          }
          if (name === 'run_finished' || name === 'run_failed' || name === 'run_interrupted') {
            void refreshSession(id);
            // FRONT-1681 (review round): the chat twin
            // (inbox-chat-modal.tsx) re-marks a background run seen the
            // moment it completes WHILE the modal is open, not only on
            // attach — otherwise a build the user is actively watching
            // finish would sit unseen (green) the instant this terminal
            // event lands, until the window is closed and reopened.
            //
            // FRONT-1681 (third review round): the stream replays the WHOLE
            // tape from `after=0` on every attach, so this listener fires
            // once per HISTORICAL terminal event too, not just the live
            // one — opening a build with a dozen prior runs used to fire a
            // dozen redundant seen-POSTs (each invalidating the coding
            // list), on top of the attach effect's own mark-seen call one
            // line above this hook's return. Gate on the event's `seq`
            // against the tape's high-water mark AS OF THIS ATTACH
            // (`attachLastSeqRef`, from the same GET's `lastSeq` the attach
            // effect already fetches) — only a seq past that boundary is a
            // genuinely NEW event arriving while this window is open.
            // `attachLastSeqRef.current == null` means the boundary isn't
            // known yet (the GET hasn't resolved) — deliberately skip
            // rather than guess, since the attach effect's own mark-seen
            // call already covers "the user opened this build" regardless.
            //
            // Why this is safe to gate on VALUE rather than commit order:
            // `lastRunEndedAt` (what the seen comparison reads server-side,
            // src/coding/seen.ts) is a `new Date()` captured in the runner
            // microseconds after the SAME terminal event is appended to the
            // tape (src/coding/store.ts's `statusFields`, via `endCodingSessionRun`) — so
            // by construction it is always a point in time BEFORE this
            // browser can possibly have received the event (which requires
            // the append to already be visible on the tape, plus a network
            // round trip). Any `seenAt` this mark-seen call produces is
            // therefore always later than the compared value, regardless of
            // when the status row's own write actually commits. That
            // argument depends on the stamp staying a prompt, synchronous
            // `new Date()` at the moment the run ends — switching it to a
            // SQL `now()` evaluated by a DEFERRED write, or moving the
            // status write to run after `maybeAutoClear` (which makes an
            // LLM call and can take seconds), breaks it and reopens this bug.
            if (attachLastSeqRef.current != null && seq > attachLastSeqRef.current) {
              markSeen.mutate(id);
            }
          }
        });
      }

      es.onerror = () => {
        // Either the server closed the idle stream (expected) or the
        // connection dropped. Take over from the native retry.
        es.close();
        if (esRef.current === es) esRef.current = null;
        if (attachedIdRef.current !== id || !workingRef.current) return;
        const delay = backoffRef.current;
        backoffRef.current = Math.min(backoffRef.current * 2, RECONNECT_MAX_MS);
        reconnectTimerRef.current = window.setTimeout(() => {
          reconnectTimerRef.current = null;
          if (attachedIdRef.current === id && !esRef.current) openStream(id, cursorRef.current);
        }, delay);
      };
    },
    // markSeen is a stable mutation handle (useMutation) — deliberately
    // excluded, same convention as the attach effect below. Including it
    // would risk `openStream`'s own identity churning across renders,
    // which would re-fire the attach effect (deps include `openStream`)
    // on every render instead of only on a real session switch.
    [closeStream, refreshSession],
  );

  // Attach: reset local tape, load the row, replay the stream from 0.
  useEffect(() => {
    attachedIdRef.current = sessionId;
    setSession(null);
    setEvents([]);
    setLoadError(null);
    cursorRef.current = 0;
    backoffRef.current = RECONNECT_BASE_MS;
    attachLastSeqRef.current = null;
    setOpenedAtSeq(null);
    setSummary({ text: null, refreshed: false, pending: false });
    if (!sessionId) {
      closeStream();
      return;
    }
    // FRONT-1681 (build lifecycle round): attaching to a session IS opening
    // it — the same "open = seen" moment `useMarkChatSeen` fires on for a
    // chat (inbox-chat-modal.tsx). This is the one seam every consumer of
    // this hook attaches through (the coding window's default-select AND
    // opening a build from the chat-overview board both land here via
    // `sessionId` changing), so it also covers "opening a build from the
    // board must mark it seen too" without a second call site.
    markSeen.mutate(sessionId);
    let cancelled = false;
    api
      .get<SessionResponse>(`/api/coding/sessions/${sessionId}`)
      .then((data) => {
        if (!cancelled && attachedIdRef.current === sessionId) {
          setSession(onLocalClock(data));
          // FRONT-1681 (third review round): the boundary the SSE listener
          // above gates its own redundant mark-seen calls on — see that
          // listener's comment.
          attachLastSeqRef.current = data.lastSeq;
          setOpenedAtSeq(data.lastSeq);
          // FRONT-3175: once per open. The server only spends a model call
          // when a run started since the stored summary; otherwise it hands
          // back what it has.
          const stored = data.session.summary ?? null;
          setSummary({ text: stored, refreshed: false, pending: data.lastSeq > 0 });
          if (data.lastSeq > 0) {
            api
              .post<SummaryResponse>(`/api/coding/sessions/${sessionId}/summary`, {})
              .then((r) => {
                if (attachedIdRef.current !== sessionId) return;
                setSummary({ text: r.summary ?? stored, refreshed: r.refreshed && !!r.summary, pending: false });
              })
              .catch(() => {
                if (attachedIdRef.current === sessionId) setSummary({ text: stored, refreshed: false, pending: false });
              });
          }
        }
      })
      .catch((err) => {
        if (!cancelled && attachedIdRef.current === sessionId) setLoadError((err as Error).message);
      });
    openStream(sessionId, 0);
    return () => {
      cancelled = true;
      closeStream();
    };
    // markSeen is a stable mutation handle (useMutation); excluded to avoid
    // a re-fire loop, same convention as inbox-chat-modal.tsx's own
    // markChatSeen effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, openStream, closeStream]);

  const transcript = useMemo(() => buildTranscript(events), [events]);
  // The top bar's own poll of the session list (shared cache, never fetched
  // from here): the server's word on whether the run is still alive.
  const { data: serverRow } = useQuery({
    queryKey: codingKeys.list(),
    queryFn: () => api.get<{ sessions: CodingSessionView[] }>('/api/coding/sessions'),
    enabled: false,
    select: (d) => d.sessions.find((x) => x.id === sessionId),
  });
  const status = useMemo(
    () => reconcileWithServerLiveness(deriveStatus(session?.status, events), serverRow),
    [session?.status, events, serverRow],
  );
  const waitingReason = useMemo(() => deriveWaitingReason(events), [events]);
  const pendingQuestion = useMemo(() => hasUnansweredQuestion(events), [events]);
  // FRONT-1708: the batch a run is actually blocked on right now, if any —
  // NOT the same as "there's an unanswered card on screen" (findOpenQuestionItem
  // covers that). Drives the composer's send routing and placeholder in
  // coding-session-modal.tsx. FRONT-1850: pass session?.status so a
  // boot-orphaned, already-failed session never reads as still blocked.
  const blockingQuestion = useMemo(
    () => findBlockingQuestionItem(transcript, events, session?.status),
    [transcript, events, session?.status],
  );
  // FRONT-3084: the background check still writes to the tape, so the stream reconnects for it too.
  workingRef.current = status === 'working' || status === 'checking';

  // FRONT-1898: the composer's "should Stop show / can the user send" signal
  // must free the instant the user clicks Stop, not once `status` (the tape)
  // or the server poll catches up — mirrors chat's `isTurnStreaming`
  // (turn-busy.ts), which gates the same way on its own `manuallyStopped`.
  const composerBusy = status === 'working' && !manuallyStopped;

  // FRONT-1898: raw server-truth signal, UNGATED by manuallyStopped — this
  // is what the self-heal poll below trusts, so it keeps re-checking the
  // server for as long as the backend genuinely might still be running,
  // regardless of what the composer is optimistically showing.
  const rawQueueBusy = computeQueueBusy(status, session?.status, pendingQuestion);

  // FRONT-1898: the composer-facing version, which DOES fold in the
  // override — mirrors chat's `isQueueBusy` (turn-busy.ts). Without this, a
  // queued follow-up waits on `session.status` leaving "running" even after
  // the user has already told the run to stop, which can lag by however
  // long the abandoned run takes to notice the abort (or never, if the
  // interrupt silently no-ops) — reads to the user as the message being
  // swallowed.
  const queueBusy = rawQueueBusy && !manuallyStopped;

  // FRONT-1898 (DerStoffel review finding): the state word and elapsed
  // timer must ALSO reflect the user's stop intent immediately — otherwise
  // Stop frees the Send button while the header keeps pulsing "working"
  // with the timer still counting up, indefinitely on a wedged run.
  // Reuses the exact "stopped" wording `deriveWaitingReason` already
  // produces once the tape's own `run_interrupted` event lands, so the
  // optimistic display and the later-confirmed one never visibly jump.
  const displayStatus: CodingStatus | null =
    manuallyStopped && status === 'working' ? 'waiting' : status;
  const displayWaitingReason: WaitingReason | null =
    manuallyStopped && status === 'working' ? 'stopped' : waitingReason;

  /**
   * BUG-037 / FRONT-850: while EITHER signal thinks a run is still going,
   * independently poll the session row every 2s — the same "second opinion"
   * shape as the inbox modal's `useBackgroundChatForSession`. The SSE
   * tape's own reconnect only retries while it still believes it's busy, so
   * a stream that drops or gets idle-closed before the terminal event lands
   * can freeze `status` on "working" forever with nothing to wake it back
   * up — `rawQueueBusy` (above) is what recovers that case, by trusting the
   * poll once it has an answer. Gating on it (rather than the raw local
   * `status`) also means a single dropped fetch (e.g. the one fired from a
   * terminal tape event via `refreshSession`, which has no retry of its
   * own) self-heals on the next 2s tick instead of stranding a stale
   * `session.status` with nothing left polling to correct it. Uses the
   * lighter `fetchSession` (not `refreshSession`) — this fires every 2s for
   * the whole run, and has no reason to force the sessions LIST to refetch
   * on every tick too.
   *
   * FRONT-1898 (DerStoffel review finding): deliberately gated on
   * `rawQueueBusy`, NOT the manuallyStopped-aware `queueBusy` — a Stop
   * click must not also silence the one thing that can confirm the run
   * actually stopped (or discover it didn't). Gating this on the optimistic
   * signal would leave `displayStatus` stuck showing "stopped" forever with
   * nothing left checking whether that's still true.
   */
  useEffect(() => {
    if (!sessionId || !rawQueueBusy) return;
    const interval = window.setInterval(() => {
      void fetchSession(sessionId);
    }, 2_000);
    return () => window.clearInterval(interval);
  }, [sessionId, rawQueueBusy, fetchSession]);

  /**
   * Send a message into the session. Attach-then-send: the stream is
   * (re)opened from the current cursor BEFORE the POST, inside the server's
   * grace window, so the run's first events are never missed.
   */
  const send = useCallback(
    async (prompt: string, attachments: ChatAttachment[] = []) => {
      if (!sessionId) return;
      // FRONT-1898: the choke point every genuinely new turn funnels
      // through — clear a stale manual-stop override from a PRIOR turn so
      // it can't leak into this one (mirrors use-agent-chat.ts's reset in
      // sendMessageResettingLiveRun).
      setManuallyStopped(false);
      if (!esRef.current) openStream(sessionId, cursorRef.current);
      try {
        await api.post(`/api/coding/sessions/${sessionId}/messages`, {
          prompt,
          // Only the id crosses the wire — the backend reads name/type/content
          // from the Files store row and stages the file into the repo.
          attachments: attachments.map((a) => ({ fileId: a.fileId })),
        });
        setSession((s) => (s ? { ...s, status: 'running', lastActivityAt: new Date().toISOString() } : s));
        queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      } catch (err) {
        // FRONT-2937: a spent budget refreshes the budget, so the thread's card and the composer lock show at once.
        const spent = budgetRefused(err, refreshBudget);
        if (spent) throw spent;
        // 409 = a run is already in flight; the open stream shows it. Other
        // failures bubble to the composer's toast.
        void refreshSession(sessionId);
        throw err;
      }
    },
    [sessionId, openStream, queryClient, refreshSession, refreshBudget],
  );

  /**
   * FRONT-2935: bring the app's latest changes into this session's draft.
   * A rework starts a turn server-side, so the stream is opened and the row
   * marked running the way `send` does; a clean update adds a note to the
   * tape, which the reopened stream delivers.
   */
  const updateDraft = useCallback(async () => {
    if (!sessionId) return null;
    setManuallyStopped(false);
    try {
      const result = await api.post<DraftUpdateResult>(`/api/coding/sessions/${sessionId}/update`, {});
      if (result.status === 'reworking') setSession((s) => (s ? { ...s, status: 'running' } : s));
      // A rework's turn, or a clean update's note, is on the tape; up-to-date added nothing.
      if (result.status !== 'up-to-date' && !esRef.current) openStream(sessionId, cursorRef.current);
      return result;
    } catch (err) {
      // FRONT-2937: the rework turn a spent budget refused, shown like a refused message.
      throw budgetRefused(err, refreshBudget) ?? err;
    } finally {
      queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      queryClient.invalidateQueries({ queryKey: codingKeys.draftUpdate(sessionId) });
    }
  }, [sessionId, openStream, queryClient, refreshBudget]);

  /**
   * FRONT-1898: sets the manual-stop override the INSTANT this is called —
   * before the network round-trip — so the composer frees immediately
   * regardless of how long the backend takes to actually honor the abort
   * (today, up to a sandbox tool call's own timeout; see the ticket). The
   * route's `{ interrupted }` result is returned honestly rather than
   * discarded, so the caller can tell the user when a Stop click landed on
   * a run this backend has no record of (instead of doing nothing visibly).
   */
  const interrupt = useCallback(async () => {
    if (!sessionId) return true;
    setManuallyStopped(true);
    const result = await api.post<{ interrupted: boolean }>(`/api/coding/sessions/${sessionId}/interrupt`, {});
    void refreshSession(sessionId);
    return result.interrupted;
  }, [sessionId, refreshSession]);

  /**
   * Remove a staged upload from the session repo (FRONT-358). The backend
   * appends an `upload_removed` event to the tape and returns it; we fold it
   * into the local tape directly because the SSE stream only tails while a
   * run is active — without this the chip wouldn't vanish until reopen.
   */
  const deleteUpload = useCallback(
    async (name: string) => {
      if (!sessionId) return;
      const result = await api.delete<{
        removed: boolean;
        event: { seq: number; name: CodingEventName; payload: Record<string, unknown> };
      }>(`/api/coding/sessions/${sessionId}/uploads/${encodeURIComponent(name)}`);
      if (result.event.seq > cursorRef.current) cursorRef.current = result.event.seq;
      setEvents((prev) => appendEvents(prev, [result.event]));
    },
    [sessionId],
  );

  /**
   * Answer (or skip) a pending ask_user question card (FRONT-707). While a
   * run is blocked on the card, the backend's polling tool consumes the
   * answers and the SSE tape delivers the answered event; when the run ended
   * on an interview timeout, the response carries the answered event (fold it
   * in, like deleteUpload) and a continuation run starts — reattach the
   * stream BEFORE posting, same attach-then-send shape as `send`.
   */
  const answerQuestions = useCallback(
    async (
      batchId: number,
      body: AnswerRequestBody,
    ) => {
      if (!sessionId) return;
      // FRONT-1898 (code-review finding): this is the SECOND choke point a
      // genuinely new run can start through — the "no active run" branch of
      // the answers route can start a continuation run (`runStarted: true`)
      // just like `send()` does. Missing this reset left `manuallyStopped`
      // stuck true forever after a Stop-then-answer sequence: the composer
      // never showed Stop again for the new run, and a queued draft could
      // auto-fire straight into it since `queueBusy` read as free.
      setManuallyStopped(false);
      if (!esRef.current) openStream(sessionId, cursorRef.current);
      let result: {
        ok: boolean;
        reason?: string;
        consumed?: 'run' | 'continuation' | 'cancelled' | 'dismissed';
        runStarted?: boolean;
        guidedBuildStopped?: boolean;
        warning?: string;
        event?: { seq: number; name: CodingEventName; payload: Record<string, unknown> };
      };
      try {
        result = await api.post(`/api/coding/sessions/${sessionId}/questions/${batchId}/answers`, body);
      } catch (err) {
        // FRONT-1160 (pr-panel finding): a 409 here (card stale / already
        // answered) still means stopGuidedBuild succeeded server-side — the
        // route flips that flag unconditionally, before it ever looks at the
        // batch. api.post throws on any non-2xx before this function gets to
        // inspect a `result.ok`, so this is the only place that can see it —
        // reflect it now or the user's "Stop asking" click looks like it
        // silently failed even though it worked.
        const flagStopped =
          err instanceof ApiError && (err.body as { guidedBuildStopped?: boolean } | undefined)?.guidedBuildStopped;
        if (flagStopped && attachedIdRef.current === sessionId) {
          setSession((s) => (s ? { ...s, guidedBuildStopped: true } : s));
        }
        // FRONT-2937: an answer that would start a turn on a spent budget recorded nothing.
        throw budgetRefused(err, refreshBudget) ?? err;
      }
      if (!result.ok) throw new Error(result.reason || 'These questions can no longer be answered.');
      // Guard every post-await mutation on the session still being attached —
      // a project switch mid-request would otherwise leak this session's
      // cursor/event/status into whichever session is now open (same guard as
      // fetchSession; matches the deleteUpload race). The caller still gets
      // the result for its own toast handling.
      if (attachedIdRef.current !== sessionId) return result;
      if (result.event) {
        if (result.event.seq > cursorRef.current) cursorRef.current = result.event.seq;
        setEvents((prev) => appendEvents(prev, [result.event!]));
      }
      if (result.runStarted) {
        setSession((s) => (s ? { ...s, status: 'running' } : s));
        queryClient.invalidateQueries({ queryKey: codingKeys.list() });
      }
      if (result.guidedBuildStopped) {
        setSession((s) => (s ? { ...s, guidedBuildStopped: true } : s));
      }
      return result;
    },
    [sessionId, openStream, queryClient, refreshBudget],
  );

  const uploads = useMemo(() => deriveUploads(events), [events]);
  const contextUsage = useMemo(() => deriveContextUsage(events), [events]);
  // FRONT-1746: both are pure reads of the same live tape the meter uses, so
  // they update from the SSE stream with no extra request — the status strip
  // moves as the run moves.
  const activity = useMemo(() => deriveRunActivity(events), [events]);
  // The finished run's shape ("5 steps · 5m 43s"), from the same tape. Null
  // while a run is in flight, which is what makes the strip fall back to the
  // live activity label rather than quoting a tally that doesn't exist yet.
  const runSummary = useMemo(() => deriveRunSummary(events), [events]);
  // FRONT-2258: the whole session's time and turns — summed across every run
  // on the tape, for the header menu's totals block.
  const sessionStats = useMemo(() => deriveSessionStats(events), [events]);
  // FRONT-2962: its cost is what the platform charged per run, which only
  // the server can read; re-read whenever a run ends.
  const endedRuns = useMemo(() => countEndedRuns(events), [events]);
  const { data: sessionCost } = useCodingSessionCost(session?.id ?? null, endedRuns);

  return {
    session,
    // FRONT-1880: re-read the attached session so the header's app facts
    // (audience, lock reason, link) reflect a change made from the Share
    // popover. Deliberately the light `fetchSession` and not
    // `refreshSession`: an audience change touches THIS session's app row,
    // not the sessions list, so invalidating the list would refetch it for
    // nothing. No-op when nothing is attached.
    refreshApp: () => {
      const id = attachedIdRef.current;
      if (id) void fetchSession(id);
    },
    transcript,
    openedAtSeq,
    summary,
    status,
    // FRONT-1898: displayStatus/displayWaitingReason are what
    // coding-session-modal.tsx's SessionStrip/BuildState must render — NOT
    // `status`/`waitingReason` — so the state word and elapsed timer free the
    // instant the user clicks Stop instead of pulsing "working" until the
    // server catches up. `status`/`waitingReason` themselves stay the raw
    // tape-derived values, unaffected by manuallyStopped, for every other
    // consumer (turnError, the failed-run banner, scroll-follow) that must
    // keep reflecting genuine server truth.
    displayStatus,
    displayWaitingReason,
    queueBusy,
    composerBusy,
    loadError,
    uploads,
    contextUsage,
    activity,
    runSummary,
    sessionStats,
    sessionCost,
    waitingReason,
    send,
    updateDraft,
    interrupt,
    deleteUpload,
    answerQuestions,
    blockingQuestion,
  };
}
